import React, { useState, useRef, useCallback } from "react";
import Popper from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ReactMarkdown from "react-markdown";
import "./CitationPopup.css";

// ---- CitationPopup ----

const CHUNK_CHAR_LIMIT = 300;

function truncateChunk(text) {
  const trimmed = text.trim();
  if (trimmed.length <= CHUNK_CHAR_LIMIT) return { text: trimmed, truncated: false };
  return { text: trimmed.slice(0, CHUNK_CHAR_LIMIT), truncated: true };
}

function CitationPopup({ citation, anchorEl, onMouseEnter, onMouseLeave }) {
  const [showAll, setShowAll] = useState(false);
  const [expandedChunks, setExpandedChunks] = useState({});
  const chunks = citation.content_snippet
    ? citation.content_snippet.split("\n---\n").filter((c) => c.trim())
    : [];
  const visibleChunks = showAll ? chunks : chunks.slice(0, 2);
  const hasMore = chunks.length > 2;

  const pages = citation.page
    ? String(citation.page).split(",").map((p) => p.trim()).filter(Boolean)
    : [];

  const toggleChunkExpand = (i) => {
    setExpandedChunks((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  // Extract short display path from URL
  let sourceDisplay = "";
  if (citation.source) {
    if (citation.source.startsWith("http")) {
      try {
        const url = new URL(citation.source);
        const path = url.pathname === "/" ? "" : url.pathname;
        sourceDisplay = url.hostname + path;
      } catch {
        sourceDisplay = citation.source;
      }
    } else {
      sourceDisplay = citation.source;
    }
  }

  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="top"
      modifiers={[
        { name: "offset", options: { offset: [0, 10] } },
        { name: "preventOverflow", options: { boundary: "viewport", padding: 12 } },
        { name: "flip", options: { fallbackPlacements: ["bottom", "top"] } },
      ]}
      style={{ zIndex: 1300 }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="citation-popup">
        {/* Source header */}
        <div className="citation-popup-header">
          {citation.source && (
            <a
              className="citation-popup-source"
              href={citation.source.startsWith("http") ? citation.source : undefined}
              target="_blank"
              rel="noopener noreferrer"
              title={citation.source}
            >
              <span className="citation-source-dot" />
              {sourceDisplay}
            </a>
          )}
          {citation.collection && (
            <span className="citation-popup-collection">{citation.collection}</span>
          )}
        </div>

        {/* Chunks */}
        <div className="citation-chunks">
          {visibleChunks.map((chunk, i) => {
            const isExpanded = expandedChunks[i];
            const { text: displayText, truncated } = truncateChunk(chunk);

            return (
              <div key={i} className="citation-chunk-card">
                {pages[i] && (
                  <span className="citation-chunk-page">Page {pages[i]}</span>
                )}
                <div className="citation-chunk-body">
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="citation-md-p">{children}</p>,
                      strong: ({ children }) => <strong className="citation-md-strong">{children}</strong>,
                      em: ({ children }) => <em>{children}</em>,
                      ul: ({ children }) => <ul className="citation-md-list">{children}</ul>,
                      ol: ({ children }) => <ol className="citation-md-list">{children}</ol>,
                      li: ({ children }) => <li className="citation-md-li">{children}</li>,
                      a: ({ href, children }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="citation-md-link">
                          {children}
                        </a>
                      ),
                      code: ({ children }) => <code className="citation-md-code">{children}</code>,
                      h1: ({ children }) => <p className="citation-md-heading">{children}</p>,
                      h2: ({ children }) => <p className="citation-md-heading">{children}</p>,
                      h3: ({ children }) => <p className="citation-md-heading">{children}</p>,
                    }}
                  >
                    {isExpanded ? chunk.trim() : displayText}
                  </ReactMarkdown>
                  {truncated && !isExpanded && (
                    <span className="citation-chunk-fade">...</span>
                  )}
                </div>
                {truncated && (
                  <Tooltip title={isExpanded ? "Show less" : "Show more"} placement="bottom">
                    <span
                      className="citation-chunk-toggle"
                      onClick={() => toggleChunkExpand(i)}
                      role="button"
                    >
                      {isExpanded ? (
                        <ExpandLessRoundedIcon style={{ fontSize: "1rem" }} />
                      ) : (
                        <ExpandMoreRoundedIcon style={{ fontSize: "1rem" }} />
                      )}
                    </span>
                  </Tooltip>
                )}
              </div>
            );
          })}
        </div>

        {/* More chunks toggle */}
        {hasMore && (
          <button
            className="citation-more-chunks"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll
              ? "Show fewer sources"
              : `+ ${chunks.length - 2} more source${chunks.length - 2 > 1 ? "s" : ""}`}
          </button>
        )}
      </div>
    </Popper>
  );
}

// ---- CitationMarker ----

function CitationMarker({ citation }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const closeTimeoutRef = useRef(null);

  const handleOpen = useCallback((e) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 200);
  }, []);

  const handlePopupEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handlePopupLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  }, []);

  return (
    <>
      <span
        className="citation-marker"
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
      >
        {citation.citation_index}
      </span>
      {anchorEl && (
        <CitationPopup
          citation={citation}
          anchorEl={anchorEl}
          onMouseEnter={handlePopupEnter}
          onMouseLeave={handlePopupLeave}
        />
      )}
    </>
  );
}

// ---- Utility: process a text string to replace [N] with CitationMarker components ----

export function processCitationMarkers(text, citations) {
  const citationMap = citations && citations.length > 0
    ? new Map(citations.map((c) => [c.citation_index, c]))
    : null;

  const parts = [];
  let lastIndex = 0;
  const regex = /\[(\d+)\]/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const index = parseInt(match[1], 10);
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (citationMap && citationMap.has(index)) {
      parts.push(
        <CitationMarker
          key={`cite-${index}-${match.index}`}
          citation={citationMap.get(index)}
        />
      );
    } else {
      parts.push(
        <span key={`cite-${index}-${match.index}`} className="citation-marker">
          {index}
        </span>
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (parts.length === 0) return text;

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

// ---- Helper: process React children to replace citation markers in text nodes ----

export function processChildren(children, citations) {
  return React.Children.map(children, (child) => {
    if (typeof child === "string") {
      return processCitationMarkers(child, citations);
    }
    return child;
  });
}

export default CitationPopup;
